import React, { useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import styled from 'styled-components';
import { theme } from '../styles/theme';
import { useArticles } from '../contexts/ArticleContext';
import { categories } from '../data/categories';
import ArticleCard from '../components/molecules/ArticleCard';
import { Container, SectionTitle } from '../styles/commonStyles';
import SEO from '../components/SEO';
import { Category } from '../types';

const CategoryContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: ${theme.space.xl} ${theme.space.md};
`;

const CategoryHeader = styled.div`
  margin-bottom: ${theme.space.xl};
  text-align: center;
  margin-top: ${theme.space.xl};
`;

const CategoryTitleWrapper = styled.div`
  margin-top: ${theme.space.xl};
  margin-bottom: ${theme.space.xl};
`;

const SubCategoryList = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: ${theme.space.sm};
  margin-bottom: ${theme.space.lg};
`;

const SubCategoryLink = styled(Link)`
  padding: ${theme.space.xs} ${theme.space.sm};
  background-color: #f3e5f5;
  color: #7b1fa2;
  border-radius: ${theme.radii.sm};
  font-size: ${theme.fontSizes.sm};
  transition: ${theme.transitions.default};
  text-decoration: none;
  
  &:hover {
    background-color: #e1bee7;
  }
`;

const ArticlesList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.space.md};
`;

const NoArticles = styled.p`
  text-align: center;
  font-size: ${theme.fontSizes.lg};
  color: ${theme.colors.lightText};
  padding: ${theme.space.xl} 0;
`;

const NotFound = styled.div`
  text-align: center;
  padding: 2rem;
  
  h2 {
    margin-bottom: 1rem;
  }
  
  a {
    color: #1976d2;
    text-decoration: none;
    
    &:hover {
      text-decoration: underline;
    }
  }
`;

const PageWrapper = styled.div`
  background-color: ${theme.colors.primary};
  color: ${theme.colors.white};
  min-height: 100vh;
  padding-bottom: ${theme.space.xl};
  position: relative;
  overflow-x: hidden;
  width: 100%;
`;

const HomeButton = styled(Link)`
  background-color: #D67616;
  color: white;
  padding: 8px 16px;
  border: none;
  border-radius: 30px;
  font-size: 14px;
  font-weight: bold;
  cursor: pointer;
  transition: background-color 0.3s;
  display: block;
  margin: 30px auto 0;
  text-align: center;
  width: fit-content;
  
  &:hover {
    background-color: #e55a00;
  }
`;

const CategoryPage: React.FC = () => {
  const { categoryName } = useParams<{ categoryName: string }>();
  const { articles, loading } = useArticles();
  
  // カテゴリが存在するか確認
  const category = categories.find(cat => cat.name === categoryName);
  
  // カテゴリに属する記事を取得
  const categoryArticles = articles.filter(article => article.category === categoryName);
  
  // カテゴリー名を日本語表示用に変換
  const displayCategoryName = useMemo(() => {
    return categoryName || '';
  }, [categoryName]);
  
  if (!category) {
    return (
      <Container>
        <SEO 
          title="カテゴリーが見つかりません"
          description="アラヨットラベル - 指定されたカテゴリーが見つかりませんでした"
        />
        <NotFound>
          <h2>カテゴリーが見つかりませんでした</h2>
          <Link to="/">ホームに戻る</Link>
        </NotFound>
      </Container>
    );
  }
  
  return (
    <PageWrapper>
      <SEO 
        title={`${displayCategoryName}カテゴリー`}
        description={`アラヨットラベル - ${displayCategoryName}に関する旅行記事一覧です。${displayCategoryName}に関する旅行情報、観光スポット、グルメ情報を提供します。`}
        keywords={`${displayCategoryName}, 旅行, 観光, カテゴリー, アラヨットラベル`}
        canonical={`/category/${categoryName}`}
      />
      
      <CategoryContainer>
        <CategoryHeader>
          <CategoryTitleWrapper>
            <SectionTitle>{categoryName}</SectionTitle>
          </CategoryTitleWrapper>
          <SubCategoryList>
            {category.subCategories.map((subCategory: string) => (
              <SubCategoryLink 
                key={subCategory} 
                to={`/category/${categoryName}/${subCategory}`}
              >
                {subCategory}
              </SubCategoryLink>
            ))}
          </SubCategoryList>
        </CategoryHeader>
        
        {categoryArticles.length > 0 ? (
          <ArticlesList>
            {categoryArticles.map(article => (
              <ArticleCard key={article.id} article={article} />
            ))}
          </ArticlesList>
        ) : (
          <NoArticles>このカテゴリの記事はまだありません。</NoArticles>
        )}
        
        <HomeButton to="/" onClick={() => window.scrollTo(0, 0)}>ホームへ戻る</HomeButton>
      </CategoryContainer>
    </PageWrapper>
  );
};

export default CategoryPage; 